"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Zap, Shield, Crown, Flower } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LImperatriceCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-green-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">Tarot</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">L'Impératrice</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot de Marseille
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Major Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">L'Impératrice</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-green-900/20 to-pink-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-l-imperatrice.svg"
                  alt="L'Impératrice Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-green-900/40 to-pink-900/40 border border-green-500/30 flex-col items-center justify-center text-green-300">
                  <span className="text-3xl font-bold mb-2">III</span>
                  <span className="text-lg font-semibold">L'IMPÉRATRICE</span>
                  <span className="text-sm text-center mt-2">The Empress</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-pink-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">III</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">L'Impératrice</h1>
                  <p className="text-xl text-green-400">Bereket / Yaratıcılık / Annelik</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğindeki üçüncü kart. Bereket, yaratıcılık ve annelik enerjisinin
                temsilcisi. Bu kart, doğal döngüleri, üretkenliği ve sezgisel bilgeliği ifade eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Bereket</span>
                <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Annelik</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Yaratıcılık</span>
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-300 rounded-full text-sm">Sezgi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bereket ve Bolluk</h3>
                  <p className="text-slate-400 text-sm">Maddi ve manevi zenginlik, doğal güzellik, bereket.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Annelik ve Koruyuculuk</h3>
                  <p className="text-slate-400 text-sm">Annelik içgüdüsü, koruyucu enerji, şefkat.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yaratıcı Enerji</h3>
                  <p className="text-slate-400 text-sm">Sanatsal yetenekler, yeni projeler, üretkenlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sezgisel Bilgelik</h3>
                  <p className="text-slate-400 text-sm">İç sesini dinleme, kadınsı sezgi, doğal bilgi.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yaratıcı Blok</h3>
                  <p className="text-slate-400 text-sm">Sanatsal engellemeler, yaratıcılığın tıkanması.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Koruyuculuk</h3>
                  <p className="text-slate-400 text-sm">Boğucu ilgi, aşırı kontrol, bağımlılık yaratma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Verimsizlik</h3>
                  <p className="text-slate-400 text-sm">İsraf, hedefsizlik, üretken olmama.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sezgisel Kopukluk</h3>
                  <p className="text-slate-400 text-sm">İç sesini kaybetme, doğal çevrimlerden kopma.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Marseille Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Taç</h3>
              <p className="text-slate-400 text-sm">Kraliçelik, otorite ve soyluluk simgesi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flower className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Doğa Motifleri</h3>
              <p className="text-slate-400 text-sm">Çiçekler, yapraklar ve bereket simgeleri.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kalkan Simgesi</h3>
              <p className="text-slate-400 text-sm">Kartal figürü, koruyuculuk ve güç.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👗</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Zengin Kıyafet</h3>
              <p className="text-slate-400 text-sm">Bolluk, lüks ve maddi zenginlik göstergesi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🪑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Taht</h3>
              <p className="text-slate-400 text-sm">Güç, otorite ve konfor sembolü.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌾</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Bereket Asa</h3>
              <p className="text-slate-400 text-sm">Doğurganlık ve yaratıcılık simgesi.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Annelik ve çocuk sahibi olma</li>
                <li>• Sevgi dolu, besleyici ilişkiler</li>
                <li>• Kadınsı enerji ve sezgi</li>
                <li>• Koruyucu ve şefkatli tutum</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yaratıcı projeler ve sanat</li>
                <li>• Kadın odaklı işler</li>
                <li>• Beslenme, güzellik, bakım sektörü</li>
                <li>• Liderlik ve yöneticilik</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kadınsı güçlere bağlanma</li>
                <li>• Yaratıcılığın keşfi</li>
                <li>• Sezgilerin geliştirilmesi</li>
                <li>• Doğal döngülere uyum</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-400" />
                Sağlık ve Refah
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Gebelik ve doğum</li>
                <li>• Hormonal denge</li>
                <li>• Beslenme ve bakım</li>
                <li>• Doğal iyileşme</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel Marseille tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/la-papesse"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            La Papesse
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/l-empereur"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            L'Empereur
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}